// «L'albero»: la vista illustrata degli ascendenti. Il disegno (tronco, rami, foglie, terreno,
// nastro) è SVG decorativo e muto per lo screen reader; le persone sono PULSANTI veri, sopra il
// disegno, con nome e ruolo: si toccano, si raggiungono con la tastiera, aprono la scheda.
//
// Si sposta col dito e si ingrandisce con due dita (niente librerie: pointer events), oppure
// con i pulsanti − / + / «Tutto l'albero» e con le frecce. Un trascinamento non apre mai una
// scheda per sbaglio: un tocco è un tocco solo se il dito si è mosso meno di 8 px.
import { el } from '../lib/ui.js';
import { parentTitle, type Ancestor } from '../lib/family-pedigree.js';
import type { AtlasPerson } from '../lib/family-atlas.js';
import { anni, ARTE, disegnaAlbero, potaVuoti } from '../lib/family-tree-art.js';

type Opzioni = {
  tree: Ancestor;
  /** Fin dove mostrare i posti vuoti (0–3). */
  vuotiFino: number;
  selected: number;
  onPerson: (id: number, trigger: HTMLElement) => void;
  onMissing: (childId: number, ruolo: 'padre' | 'madre' | 'genitore', trigger: HTMLElement) => void;
  /** Il titolo sul nastro: «L’albero di …» o «I frutti di …». */
  titolo: string;
  canAddParent: (childId: number) => boolean;
};

const NS = 'http://www.w3.org/2000/svg';
function svg<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number>, ...figli: SVGElement[]): SVGElementTagNameMap[K] {
  const n = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, String(v));
  n.append(...figli);
  return n;
}

function ritratto(p: AtlasPerson): HTMLElement {
  // Senza fotografia: le iniziali incise, come su una medaglia, non un bollino colorato.
  return el('span', { class: `albero-ritratto ${p.photoMediaId ? '' : 'is-iniziali'}`, 'aria-hidden': 'true' },
    p.photoMediaId ? el('img', { src: `/api/media/${p.photoMediaId}?w=480`, alt: '', loading: 'lazy', decoding: 'async' })
      : el('span', {}, p.name.split(/\s+/).slice(0, 2).map((s) => s[0]).join('')));
}

export function renderIllustrated(o: Opzioni): { element: HTMLElement; dispose: () => void } {
  const disegno = disegnaAlbero(potaVuoti(o.tree, o.vuotiFino));
  const { larghezza: W, altezza: H } = disegno;
  const radice = o.tree.person!;

  // --- il disegno ---
  const arte = svg('svg', { viewBox: `0 0 ${W} ${H}`, width: W, height: H, class: 'albero-arte', 'aria-hidden': 'true', focusable: 'false' });
  const defs = svg('defs', {},
    svg('path', { id: 'albero-foglia', d: 'M0,-10 C7,-6 7,6 0,10 C-7,6 -7,-6 0,-10 Z' }),
    svg('path', { id: 'albero-venatura', d: 'M0,-8 L0,8' }));
  arte.append(defs);
  arte.append(svg('ellipse', { class: 'albero-ombra', cx: disegno.nastro.x, cy: disegno.terreno + 8, rx: Math.min(W * 0.42, 340), ry: 22 }));
  const erba = svg('g', { class: 'albero-erba' });
  for (let i = -8; i <= 8; i++) {
    const x = disegno.nastro.x + i * 26, y = disegno.terreno + 6 + Math.abs(i) % 3 * 2;
    erba.append(svg('path', { d: `M${x},${y} q2,-12 5,-16 M${x + 6},${y} q0,-10 -2,-13 M${x + 10},${y} q3,-8 7,-10` }));
  }
  arte.append(erba);
  arte.append(svg('g', { class: 'albero-chioma' }, ...disegno.chioma.map((c) => svg('circle', { cx: c.x, cy: c.y, r: c.r }))));
  const rami = svg('g', { class: 'albero-rami' });
  for (const r of disegno.rami) rami.append(svg('path', { d: r.d, 'stroke-width': r.spessore, class: r.incerto ? 'is-incerto' : '' }));
  arte.append(svg('g', { class: 'albero-radici' }, ...disegno.radici.map((d) => svg('path', { d }))));
  arte.append(svg('path', { class: 'albero-tronco', d: disegno.tronco }));
  arte.append(svg('g', { class: 'albero-corteccia' }, ...disegno.corteccia.map((d) => svg('path', { d }))));
  arte.append(rami);
  // La corteccia: due righe chiare sul tronco, e sopra i rami una luce sottile.
  const luce = svg('g', { class: 'albero-luce' });
  for (const r of disegno.rami) if (!r.incerto) luce.append(svg('path', { d: r.d, 'stroke-width': Math.max(1.2, r.spessore / 5) }));
  arte.append(luce);
  const foglie = svg('g', { class: 'albero-foglie' });
  for (const f of disegno.foglie) {
    foglie.append(svg('use', { href: '#albero-foglia', class: `tono-${f.tono}`, transform: `translate(${f.x} ${f.y}) rotate(${f.angolo}) scale(${f.scala})` }));
  }
  arte.append(foglie);
  // Il nastro col titolo, ai piedi dell'albero.
  const nx = disegno.nastro.x, ny = disegno.nastro.y, nl = Math.min(W * 0.36, 250);
  arte.append(svg('g', { class: 'albero-nastro' },
    svg('path', { class: 'coda', d: `M${nx - nl - 34},${ny - 6} l34,-10 l0,34 l-34,6 l12,-15 Z` }),
    svg('path', { class: 'coda', d: `M${nx + nl + 34},${ny - 6} l-34,-10 l0,34 l34,6 l-12,-15 Z` }),
    svg('path', { class: 'fascia', d: `M${nx - nl},${ny - 16} Q${nx},${ny - 32} ${nx + nl},${ny - 16} L${nx + nl},${ny + 18} Q${nx},${ny + 2} ${nx - nl},${ny + 18} Z` })));
  const titolo = svg('text', { class: 'albero-titolo', x: nx, y: ny + 6, 'text-anchor': 'middle' });
  titolo.textContent = o.titolo;
  arte.append(titolo);

  // --- le persone ---
  const scena = el('div', { class: 'albero-scena', style: `width:${W}px;height:${H}px` }, arte);
  const byChild = new Map(disegno.posti.map((p) => [p.nodo.person?.id, p.nodo]));
  for (const { nodo, centro } of disegno.posti) {
    const figlio = nodo.childId === null ? null : byChild.get(nodo.childId)?.person ?? null;
    const ruolo = nodo.role === 'self' ? '' : `${parentTitle(nodo.role)}${figlio ? ` di ${figlio.name}` : ''}`;
    const stile = `left:${centro.x - ARTE.posto / 2}px;top:${centro.y - ARTE.medaglione / 2}px;width:${ARTE.posto}px`;
    if (!nodo.person) {
      // Un posto vuoto non è una persona: niente nome inventato, un invito.
      const puo = nodo.childId !== null && o.canAddParent(nodo.childId);
      const vuoto = el(puo ? 'button' : 'div', { class: 'albero-persona is-vuoto', style: stile, 'data-ancestor': nodo.key, ...(puo ? { type: 'button', 'aria-label': `${ruolo}: non ancora nell’albero. Raccontamelo, o collegalo` } : {}) },
        el('span', { class: 'albero-ritratto', 'aria-hidden': 'true' }, el('span', {}, '?')),
        el('span', { class: 'albero-cartiglio' }, el('span', { class: 'albero-nome' }, 'Chi era?'), el('span', { class: 'albero-anni' }, ruolo)));
      if (puo) vuoto.addEventListener('click', (e) => { if (!trascinato) o.onMissing(nodo.childId!, nodo.role === 'father' ? 'padre' : nodo.role === 'mother' ? 'madre' : 'genitore', vuoto); else e.preventDefault(); });
      scena.append(vuoto);
      continue;
    }
    const p = nodo.person, periodo = anni(p.birth, p.death);
    const b = el('button', {
      type: 'button', class: `albero-persona ${nodo.level === 0 ? 'is-radice' : ''} ${nodo.proposed ? 'is-proposto' : ''} ${p.id === o.selected ? 'is-selected' : ''}`,
      style: stile, 'data-person': String(p.id), 'data-ancestor': nodo.key, 'aria-pressed': String(p.id === o.selected),
      'aria-label': [p.name, ruolo, periodo, nodo.proposed ? 'da confermare' : ''].filter(Boolean).join(', '),
    }, ritratto(p), el('span', { class: 'albero-cartiglio' }, el('span', { class: 'albero-nome' }, p.name), periodo ? el('span', { class: 'albero-anni' }, periodo) : null));
    b.addEventListener('click', (e) => { if (trascinato) { e.preventDefault(); return; } o.onPerson(p.id, b); });
    scena.append(b);
  }

  // --- zoom e trascinamento ---
  const cornice = el('div', { class: 'albero-cornice', tabindex: '0', role: 'region', 'aria-label': `L’albero di ${radice.name}. Trascina per spostarti, due dita o i pulsanti per ingrandire, frecce da tastiera.` }, scena);
  let scala = 1, tx = 0, ty = 0, minimo = 0.2, trascinato = false, adatta = true, via = false;
  const livello = el('output', { class: 'albero-livello', 'aria-live': 'polite' }, '100%');
  const applica = (): void => {
    scena.style.transform = `translate(${tx}px, ${ty}px) scale(${scala})`;
    livello.textContent = `${Math.round(scala * 100)}%`;
  };
  const limita = (): void => {
    const cw = cornice.clientWidth, ch = cornice.clientHeight, sw = W * scala, sh = H * scala;
    tx = sw <= cw ? (cw - sw) / 2 : Math.min(40, Math.max(cw - sw - 40, tx));
    ty = sh <= ch ? (ch - sh) / 2 : Math.min(40, Math.max(ch - sh - 40, ty));
  };
  const tutto = (): void => {
    if (via || !cornice.clientWidth) return;
    adatta = true;
    scala = minimo = Math.min(1, (cornice.clientWidth - 16) / W, (cornice.clientHeight - 16) / H);
    limita(); applica();
  };
  const zoomA = (nuova: number, cx = cornice.clientWidth / 2, cy = cornice.clientHeight / 2): void => {
    adatta = false;
    const s = Math.max(minimo * 0.9, Math.min(2.5, nuova));
    tx = cx - ((cx - tx) / scala) * s; ty = cy - ((cy - ty) / scala) * s; scala = s;
    limita(); applica();
  };
  const dita = new Map<number, { x: number; y: number }>();
  let inizio: { x: number; y: number; tx: number; ty: number; dist: number; scala: number; mx: number; my: number } | null = null;
  const stato = (): typeof inizio => {
    const pts = [...dita.values()];
    const mx = pts.reduce((s, p) => s + p.x, 0) / pts.length, my = pts.reduce((s, p) => s + p.y, 0) / pts.length;
    const dist = pts.length > 1 ? Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y) : 0;
    return { x: mx, y: my, tx, ty, dist, scala, mx, my };
  };
  cornice.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const r = cornice.getBoundingClientRect();
    dita.set(e.pointerId, { x: e.clientX - r.left, y: e.clientY - r.top });
    if (dita.size === 1) trascinato = false;
    inizio = stato();
  });
  cornice.addEventListener('pointermove', (e) => {
    if (!dita.has(e.pointerId) || !inizio) return;
    const r = cornice.getBoundingClientRect();
    dita.set(e.pointerId, { x: e.clientX - r.left, y: e.clientY - r.top });
    const ora = stato()!;
    if (!trascinato && Math.hypot(ora.mx - inizio.mx, ora.my - inizio.my) < 8 && dita.size < 2) return;
    if (!trascinato) { trascinato = true; cornice.setPointerCapture(e.pointerId); }
    adatta = false;
    if (dita.size > 1 && inizio.dist > 0) {
      const s = Math.max(minimo * 0.9, Math.min(2.5, inizio.scala * (ora.dist / inizio.dist)));
      tx = ora.mx - ((inizio.mx - inizio.tx) / inizio.scala) * s; ty = ora.my - ((inizio.my - inizio.ty) / inizio.scala) * s; scala = s;
    } else { tx = inizio.tx + (ora.mx - inizio.mx); ty = inizio.ty + (ora.my - inizio.my); }
    limita(); applica();
  });
  const lascia = (e: PointerEvent): void => {
    dita.delete(e.pointerId);
    inizio = dita.size ? stato() : null;
    // Il click arriva dopo il pointerup: il flag resta vero ancora un giro.
    if (!dita.size && trascinato) setTimeout(() => { trascinato = false; }, 0);
  };
  cornice.addEventListener('pointerup', lascia);
  cornice.addEventListener('pointercancel', lascia);
  cornice.addEventListener('wheel', (e) => {
    if (!e.ctrlKey && !e.metaKey) return; // la rotellina da sola fa scorrere la pagina
    e.preventDefault();
    const r = cornice.getBoundingClientRect();
    zoomA(scala * (e.deltaY < 0 ? 1.12 : 1 / 1.12), e.clientX - r.left, e.clientY - r.top);
  }, { passive: false });
  cornice.addEventListener('keydown', (e) => {
    if (e.target !== cornice) return;
    const passo = 60;
    const mosse: Record<string, [number, number]> = { ArrowLeft: [passo, 0], ArrowRight: [-passo, 0], ArrowUp: [0, passo], ArrowDown: [0, -passo] };
    if (mosse[e.key]) { e.preventDefault(); adatta = false; tx += mosse[e.key]![0]; ty += mosse[e.key]![1]; limita(); applica(); }
    else if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomA(scala * 1.25); }
    else if (e.key === '-') { e.preventDefault(); zoomA(scala / 1.25); }
  });
  // Un medaglione raggiunto con Tab fuori inquadratura viene portato dentro.
  scena.addEventListener('focusin', (e) => {
    const n = (e.target as HTMLElement).closest<HTMLElement>('.albero-persona');
    if (!n) return;
    const r = n.getBoundingClientRect(), f = cornice.getBoundingClientRect();
    if (r.left >= f.left && r.right <= f.right && r.top >= f.top && r.bottom <= f.bottom) return;
    tx += f.left + f.width / 2 - (r.left + r.width / 2); ty += f.top + f.height / 2 - (r.top + r.height / 2);
    adatta = false; limita(); applica();
  });
  const comando = (etichetta: string, testo: string, azione: () => void): HTMLButtonElement =>
    el('button', { type: 'button', class: 'atlas-button albero-comando', 'aria-label': etichetta, click: azione }, testo);
  const comandi = el('div', { class: 'albero-comandi' },
    comando('Rimpicciolisci l’albero', '−', () => zoomA(scala / 1.25)), livello, comando('Ingrandisci l’albero', '+', () => zoomA(scala * 1.25)),
    comando('Mostra tutto l’albero', 'Tutto l’albero', tutto));
  // All'apertura: tutto l'albero se si legge; se no (un telefono, quattro generazioni) si parte
  // già leggibili, col tronco e la persona di partenza in basso al centro, e si esplora col dito.
  const radiceX = disegno.posti[0]!.centro.x;
  const iniziale = (): void => {
    tutto();
    if (scala >= 0.45 || via || !cornice.clientWidth) return;
    adatta = false;
    scala = 0.5;
    tx = cornice.clientWidth / 2 - radiceX * scala;
    ty = cornice.clientHeight - H * scala - 8;
    limita(); applica();
  };
  let prima = true;
  const osserva = new ResizeObserver(() => { if (prima) { prima = false; iniziale(); } else if (adatta) tutto(); else { limita(); applica(); } });
  osserva.observe(cornice);
  const element = el('div', { class: 'albero-illustrato' }, comandi, cornice,
    el('p', { class: 'atlas-hint' }, 'Tocca una persona per la sua scheda. I medaglioni con il punto di domanda sono posti ancora vuoti: nessuno è stato aggiunto all’archivio.'));
  return { element, dispose: () => { via = true; osserva.disconnect(); } };
}
